import * as THREE from "three";
import { mulberry32, principalAxes } from "./linalg";

/**
 * STL files repeat every vertex once per triangle. Weld identical positions into an
 * indexed mesh so vertex normals are smooth, neighbours are real surface neighbours and
 * every distance query is made once per surface point.
 */
export function weldGeometry(
  source: THREE.BufferGeometry,
  toleranceMm = 1e-4
): THREE.BufferGeometry {
  const pos = source.getAttribute("position") as THREE.BufferAttribute;
  const srcIndex = source.index;
  const cornerCount = srcIndex ? srcIndex.count : pos.count;
  const inv = 1 / toleranceMm;
  const lookup = new Map<string, number>();
  const outPos: number[] = [];
  const remap = new Uint32Array(pos.count);

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const key = `${Math.round(x * inv)},${Math.round(y * inv)},${Math.round(z * inv)}`;
    let id = lookup.get(key);
    if (id === undefined) {
      id = outPos.length / 3;
      lookup.set(key, id);
      outPos.push(x, y, z);
    }
    remap[i] = id;
  }

  const indices: number[] = [];
  for (let c = 0; c + 2 < cornerCount; c += 3) {
    const a = remap[srcIndex ? srcIndex.getX(c) : c];
    const b = remap[srcIndex ? srcIndex.getX(c + 1) : c + 1];
    const d = remap[srcIndex ? srcIndex.getX(c + 2) : c + 2];
    if (a === b || b === d || a === d) continue;
    indices.push(a, b, d);
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(outPos, 3));
  out.setIndex(indices);
  out.computeVertexNormals();
  out.computeBoundingBox();
  out.computeBoundingSphere();
  return out;
}

const triangleCorner = (
  geometry: THREE.BufferGeometry,
  face: number,
  corner: number
): number => (geometry.index ? geometry.index.getX(face * 3 + corner) : face * 3 + corner);

export const faceCount = (geometry: THREE.BufferGeometry): number => {
  const pos = geometry.getAttribute("position");
  return Math.floor((geometry.index ? geometry.index.count : pos.count) / 3);
};

const areaCache = new WeakMap<THREE.BufferGeometry, Float32Array>();

/** One third of the area of every adjacent triangle, per vertex (mm²). Invariant to rigid motion. */
export function getVertexAreas(geometry: THREE.BufferGeometry): Float32Array {
  const cached = areaCache.get(geometry);
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  if (cached && cached.length === pos.count) return cached;

  const areas = new Float32Array(pos.count);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const faces = faceCount(geometry);
  for (let f = 0; f < faces; f++) {
    const ia = triangleCorner(geometry, f, 0);
    const ib = triangleCorner(geometry, f, 1);
    const ic = triangleCorner(geometry, f, 2);
    a.fromBufferAttribute(pos, ia);
    b.fromBufferAttribute(pos, ib);
    c.fromBufferAttribute(pos, ic);
    const area = b.sub(a).cross(c.sub(a)).length() * 0.5;
    const share = area / 3;
    areas[ia] += share;
    areas[ib] += share;
    areas[ic] += share;
  }
  areaCache.set(geometry, areas);
  return areas;
}

export interface VertexAdjacency {
  offsets: Uint32Array;
  neighbors: Uint32Array;
}

const adjacencyCache = new WeakMap<THREE.BufferGeometry, VertexAdjacency>();

/** Vertex neighbours via shared triangle edges (CSR). Null for non-indexed meshes. */
export function getVertexAdjacency(geometry: THREE.BufferGeometry): VertexAdjacency | null {
  const index = geometry.index;
  if (!index) return null;
  const pos = geometry.getAttribute("position");
  const cached = adjacencyCache.get(geometry);
  if (cached && cached.offsets.length === pos.count + 1) return cached;

  const n = pos.count;
  const degree = new Uint32Array(n);
  const faces = Math.floor(index.count / 3);
  for (let f = 0; f < faces; f++) {
    degree[index.getX(f * 3)] += 2;
    degree[index.getX(f * 3 + 1)] += 2;
    degree[index.getX(f * 3 + 2)] += 2;
  }
  const offsets = new Uint32Array(n + 1);
  for (let i = 0; i < n; i++) offsets[i + 1] = offsets[i] + degree[i];
  const neighbors = new Uint32Array(offsets[n]);
  const fill = offsets.slice(0, n);
  for (let f = 0; f < faces; f++) {
    const a = index.getX(f * 3);
    const b = index.getX(f * 3 + 1);
    const c = index.getX(f * 3 + 2);
    neighbors[fill[a]++] = b;
    neighbors[fill[a]++] = c;
    neighbors[fill[b]++] = a;
    neighbors[fill[b]++] = c;
    neighbors[fill[c]++] = a;
    neighbors[fill[c]++] = b;
  }
  const adjacency = { offsets, neighbors };
  adjacencyCache.set(geometry, adjacency);
  return adjacency;
}

/** Warn when an STL is clearly not in millimetres. */
export function checkScanUnits(geometry: THREE.BufferGeometry, label: string): string | null {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const size = new THREE.Vector3();
  geometry.boundingBox!.getSize(size);
  const diag = size.length();
  if (!Number.isFinite(diag) || diag === 0) return `${label} scan is empty or invalid.`;
  if (diag < 8) {
    return `${label} scan is only ${diag.toFixed(2)} units across — it looks like inches, centimetres or metres. Bite Finder expects millimetres; contact values will be wrong.`;
  }
  if (diag > 300) {
    return `${label} scan is ${diag.toFixed(0)} units across — larger than any dental arch in millimetres. Check the export units.`;
  }
  return null;
}

export interface SurfaceSamples {
  count: number;
  points: Float32Array;
  normals: Float32Array;
}

/** Area-weighted random surface samples with their face normals (seeded, reproducible). */
export function sampleSurfaceAreaWeighted(
  geometry: THREE.BufferGeometry,
  count: number,
  seed = 1
): SurfaceSamples {
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const faces = faceCount(geometry);
  const cumulative = new Float64Array(faces);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  let total = 0;
  for (let f = 0; f < faces; f++) {
    a.fromBufferAttribute(pos, triangleCorner(geometry, f, 0));
    b.fromBufferAttribute(pos, triangleCorner(geometry, f, 1));
    c.fromBufferAttribute(pos, triangleCorner(geometry, f, 2));
    total += e1.subVectors(b, a).cross(e2.subVectors(c, a)).length() * 0.5;
    cumulative[f] = total;
  }

  const take = total > 0 ? count : 0;
  const points = new Float32Array(take * 3);
  const normals = new Float32Array(take * 3);
  const rand = mulberry32(seed);
  for (let s = 0; s < take; s++) {
    const r = rand() * total;
    let lo = 0;
    let hi = faces - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (cumulative[mid] < r) lo = mid + 1;
      else hi = mid;
    }
    a.fromBufferAttribute(pos, triangleCorner(geometry, lo, 0));
    b.fromBufferAttribute(pos, triangleCorner(geometry, lo, 1));
    c.fromBufferAttribute(pos, triangleCorner(geometry, lo, 2));
    let u = rand();
    let v = rand();
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const w = 1 - u - v;
    points[s * 3] = a.x * w + b.x * u + c.x * v;
    points[s * 3 + 1] = a.y * w + b.y * u + c.y * v;
    points[s * 3 + 2] = a.z * w + b.z * u + c.z * v;
    e1.subVectors(b, a).cross(e2.subVectors(c, a)).normalize();
    normals[s * 3] = e1.x;
    normals[s * 3 + 1] = e1.y;
    normals[s * 3 + 2] = e1.z;
  }
  return { count: take, points, normals };
}

const faceScratch = {
  a: new THREE.Vector3(),
  b: new THREE.Vector3(),
  c: new THREE.Vector3(),
};

/** Unit face normal of triangle `face` in the geometry's local frame. */
export function faceNormal(
  geometry: THREE.BufferGeometry,
  face: number,
  out: THREE.Vector3
): THREE.Vector3 {
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const { a, b, c } = faceScratch;
  a.fromBufferAttribute(pos, triangleCorner(geometry, face, 0));
  b.fromBufferAttribute(pos, triangleCorner(geometry, face, 1));
  c.fromBufferAttribute(pos, triangleCorner(geometry, face, 2));
  return out.subVectors(b, a).cross(c.sub(a)).normalize();
}

export { triangleCorner };

export interface CombinedJawSplit {
  upper: THREE.BufferGeometry;
  lower: THREE.BufferGeometry;
  /** "components" = separate shells in the file; "plane" = fused mesh cut at the thinnest slab. */
  method: "components" | "plane";
}

function extractFaces(geometry: THREE.BufferGeometry, keep: (face: number) => boolean) {
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const faces = faceCount(geometry);
  const out: number[] = [];
  for (let f = 0; f < faces; f++) {
    if (!keep(f)) continue;
    for (let k = 0; k < 3; k++) {
      const v = triangleCorner(geometry, f, k);
      out.push(pos.getX(v), pos.getY(v), pos.getZ(v));
    }
  }
  const soup = new THREE.BufferGeometry();
  soup.setAttribute("position", new THREE.Float32BufferAttribute(out, 3));
  const welded = weldGeometry(soup);
  soup.dispose();
  return welded;
}

/**
 * Split a single STL holding both arches into upper and lower meshes. Separate shells
 * are grouped by 2-means on their centroids; a fused mesh is cut across the axis with
 * the thinnest surface slab between the two halves. The larger half (palate) is
 * reported as upper — callers should let the user swap. Returns null when no clean
 * two-way split exists.
 */
export function splitCombinedJaws(source: THREE.BufferGeometry): CombinedJawSplit | null {
  const geo = source.index ? source : weldGeometry(source);
  const pos = geo.getAttribute("position") as THREE.BufferAttribute;
  const faces = faceCount(geo);
  if (faces < 100) return null;

  const parent = new Int32Array(pos.count);
  for (let i = 0; i < parent.length; i++) parent[i] = i;
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]];
      x = parent[x];
    }
    return x;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  for (let f = 0; f < faces; f++) {
    const a = triangleCorner(geo, f, 0);
    union(a, triangleCorner(geo, f, 1));
    union(a, triangleCorner(geo, f, 2));
  }

  const { a, b, c } = faceScratch;
  const faceArea = new Float32Array(faces);
  const faceCentroid = new Float32Array(faces * 3);
  const faceComp = new Int32Array(faces);
  const comps = new Map<number, { area: number; cx: number; cy: number; cz: number }>();
  let totalArea = 0;
  for (let f = 0; f < faces; f++) {
    a.fromBufferAttribute(pos, triangleCorner(geo, f, 0));
    b.fromBufferAttribute(pos, triangleCorner(geo, f, 1));
    c.fromBufferAttribute(pos, triangleCorner(geo, f, 2));
    const cx = (a.x + b.x + c.x) / 3;
    const cy = (a.y + b.y + c.y) / 3;
    const cz = (a.z + b.z + c.z) / 3;
    const area = b.sub(a).cross(c.sub(a)).length() * 0.5;
    faceArea[f] = area;
    faceCentroid[f * 3] = cx;
    faceCentroid[f * 3 + 1] = cy;
    faceCentroid[f * 3 + 2] = cz;
    const root = find(triangleCorner(geo, f, 0));
    faceComp[f] = root;
    const s = comps.get(root) ?? { area: 0, cx: 0, cy: 0, cz: 0 };
    s.area += area;
    s.cx += cx * area;
    s.cy += cy * area;
    s.cz += cz * area;
    comps.set(root, s);
    totalArea += area;
  }

  const balanced = (areaA: number) => {
    const share = areaA / Math.max(1e-9, totalArea);
    return share > 0.2 && share < 0.8;
  };
  const finish = (inA: (face: number) => boolean, method: CombinedJawSplit["method"]) => {
    let areaA = 0;
    for (let f = 0; f < faces; f++) if (inA(f)) areaA += faceArea[f];
    if (!balanced(areaA)) return null;
    const partA = extractFaces(geo, inA);
    const partB = extractFaces(geo, (f) => !inA(f));
    return areaA >= totalArea - areaA
      ? { upper: partA, lower: partB, method }
      : { upper: partB, lower: partA, method };
  };

  // Separate shells: 2-means on area-weighted component centroids.
  const significant = [...comps.entries()]
    .map(([root, s]) => ({
      root,
      area: s.area,
      centre: new THREE.Vector3(s.cx / s.area, s.cy / s.area, s.cz / s.area),
    }))
    .filter((s) => s.area > totalArea * 0.01)
    .sort((x, y) => y.area - x.area);
  if (significant.length >= 2) {
    // Widest gap (relative to spread) between sorted component centroids along each
    // principal axis — the stacking direction of the two arches wins over arch width.
    const flat = new Float32Array(significant.length * 3);
    const weights = new Float32Array(significant.length);
    significant.forEach((s, i) => {
      flat[i * 3] = s.centre.x;
      flat[i * 3 + 1] = s.centre.y;
      flat[i * 3 + 2] = s.centre.z;
      weights[i] = s.area;
    });
    const axes =
      significant.length >= 3
        ? principalAxes(flat, significant.length, weights).vectors
        : [significant[1].centre.clone().sub(significant[0].centre).normalize()];
    let bestSplit: { axis: THREE.Vector3; cut: number; score: number } | null = null;
    for (const axis of axes) {
      const proj = significant
        .map((s) => ({ t: s.centre.dot(axis), area: s.area }))
        .sort((x, y) => x.t - y.t);
      const mean = proj.reduce((m, p) => m + p.t * p.area, 0) / totalArea;
      const spread = Math.sqrt(proj.reduce((v, p) => v + p.area * (p.t - mean) ** 2, 0) / totalArea) || 1;
      let below = 0;
      for (let i = 0; i + 1 < proj.length; i++) {
        below += proj[i].area;
        if (!balanced(below)) continue;
        const score = (proj[i + 1].t - proj[i].t) / spread;
        if (!bestSplit || score > bestSplit.score) {
          bestSplit = { axis, cut: (proj[i].t + proj[i + 1].t) / 2, score };
        }
      }
    }
    if (bestSplit) {
      const { axis, cut } = bestSplit;
      const side = new Map(significant.map((s) => [s.root, s.centre.dot(axis) < cut]));
      const inA = (f: number) =>
        side.get(faceComp[f]) ??
        faceCentroid[f * 3] * axis.x + faceCentroid[f * 3 + 1] * axis.y + faceCentroid[f * 3 + 2] * axis.z < cut;
      const split = finish(inA, "components");
      if (split) return split;
    }
  }

  // Fused mesh: cut across the principal axis with the emptiest middle slab.
  const pca = principalAxes(faceCentroid, faces, faceArea);
  const BINS = 64;
  let best: { axis: THREE.Vector3; cut: number; score: number } | null = null;
  for (const axis of pca.vectors) {
    let lo = Infinity;
    let hi = -Infinity;
    const proj = new Float32Array(faces);
    for (let f = 0; f < faces; f++) {
      proj[f] =
        faceCentroid[f * 3] * axis.x + faceCentroid[f * 3 + 1] * axis.y + faceCentroid[f * 3 + 2] * axis.z;
      lo = Math.min(lo, proj[f]);
      hi = Math.max(hi, proj[f]);
    }
    const width = (hi - lo) / BINS;
    if (width <= 0) continue;
    // Spread each face's area over the span its vertices cover; binning centroids alone
    // leaves artificial empty bins between rows of a regular mesh.
    const hist = new Float64Array(BINS);
    for (let f = 0; f < faces; f++) {
      let fMin = Infinity;
      let fMax = -Infinity;
      for (let k = 0; k < 3; k++) {
        const v = triangleCorner(geo, f, k);
        const t = pos.getX(v) * axis.x + pos.getY(v) * axis.y + pos.getZ(v) * axis.z;
        fMin = Math.min(fMin, t);
        fMax = Math.max(fMax, t);
      }
      const b0 = Math.max(0, Math.floor((fMin - lo) / width));
      const b1 = Math.min(BINS - 1, Math.floor((fMax - lo) / width));
      const span = Math.max(fMax - fMin, 1e-9);
      for (let k = b0; k <= b1; k++) {
        const overlap =
          b0 === b1 ? span : Math.min(fMax, lo + (k + 1) * width) - Math.max(fMin, lo + k * width);
        hist[k] += faceArea[f] * (Math.max(0, overlap) / span);
      }
    }
    const mean = totalArea / BINS;
    for (let k = Math.floor(BINS * 0.3); k <= Math.ceil(BINS * 0.7); k++) {
      const score = hist[k] / mean;
      if (!best || score < best.score) best = { axis, cut: lo + (k + 0.5) * width, score };
    }
  }
  if (!best || best.score > 0.6) return null;
  const { axis, cut } = best;
  return finish(
    (f) =>
      faceCentroid[f * 3] * axis.x + faceCentroid[f * 3 + 1] * axis.y + faceCentroid[f * 3 + 2] * axis.z < cut,
    "plane"
  );
}
